import type {
  CommissionCategory,
  TransportRateLookupRow,
} from '@travel-planner/shared/pricing';
import type {
  PlannerDayService,
  PlannerPricingRow,
  PlannerServiceRates,
  PlannerServiceSpan,
  PlannerTransportVehicle,
} from '@travel-planner/shared/utils/planner-day-services';
import type {
  PlannerTransferKind,
  PlannerTransferSpot,
} from '@travel-planner/shared/utils/planner-transfers';
import type { FocusEvent, ReactNode } from 'react';
import type {
  AdvisorDraft,
  AdvisorDraftDay,
  AdvisorDraftItem,
  AdvisorDraftLeg,
  AdvisorGuests,
  AdvisorHotelBooking,
  AdvisorRegion,
} from '../model/types';
import type {
  ItemClock,
  PlannerTravellers,
  PricedTransfer,
} from '../model/v3-itinerary';
import type { DaySuggestions } from '../model/v3-suggestions';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toUsdEstimate } from '@travel-planner/shared/utils/fx-convert';
import { usableAvgRoomNight } from '@travel-planner/shared/utils/hotel-avg-room-night';
import {
  plannerDayDateLabel,
  plannerDayGroupSize,
  plannerServiceLabel,
  plannerServiceRate,
  plannerServiceRates,
  plannerTransportSeats,
  plannerVehicle,
  plannerVehicleOf,
  tourNeedsCar,
} from '@travel-planner/shared/utils/planner-day-services';
import { plannerTransferLabel } from '@travel-planner/shared/utils/planner-transfers';
import { GripVertical, Info, MessageSquare, User } from 'lucide-react';
import { useRef, useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { AvgRoomNightPrice } from '@/shared/ui/avg-room-night-price';
import { chapterNights } from '../model/add-item';
import { guestsLabel } from '../model/card-facts';
import {
  commissionCategoryOfDayService,
  estimateAgencyCommissionByCategory,
  usePlannerCommissionRates,
} from '../model/commission';
import { isDepartureDay, nightDays } from '../model/departure-day';
import {
  ADVISOR_RAIL_DROP_ID,
  ADVISOR_TOTALS_ID,
  advisorDayDropId,
  advisorItemDragId,
} from '../model/dnd';
import { draftHasSuggested } from '../model/merge-generate';
import { regionName } from '../model/region-layout';
import { advisorHotelBookings } from '../model/types';
import {
  DAY_LIMIT_MINUTES,
  dayItemClocks,
  dayMinutes,
  dayServiceContext,
  dayServices,
  estimatedTotal,
  estimateNote,
  formatHours,
  hotelCheckOuts,
  itemsByClock,
  itineraryAlerts,
  itinerarySummary,
  tripTransfers,
} from '../model/v3-itinerary';
import {
  chapterHotelPoint,
  formatDuration,
  formatKm,
  haversineKm,
  priceLabel,
} from '../model/v3-results';
import { useOptionalPlannerHost } from './planner-host';
import { PlannerV3ItemDetails } from './planner-v3-item-details';
import { ItemTimeEditor, ItemTimePill } from './planner-v3-item-time';
import css from './planner-v3-itinerary.module.scss';
import { PriceText } from './planner-v3-price';
import { TransferPlace } from './planner-v3-transfer-place';
import { TransportVehicles } from './planner-v3-transport-vehicles';
import { UsdEstimate } from './planner-v3-usd';
import { useCommissionOnTop } from './use-commission-on-top';

const dayCss = pickCss(css, css.day);
const hoursCss = pickCss(css, css.hours);
const itemCss = pickCss(css, css.item);
const emptyCss = pickCss(css, css.empty);
const linkCss = pickCss(css, css.link);

const NONE_PENDING: ReadonlySet<string> = new Set();

const NO_PRICING: readonly PlannerPricingRow[] = [];
const NO_TRANSPORT_RATES: readonly TransportRateLookupRow[] = [];

const USD_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const usd = (amount: number | null) => USD_FORMAT.format(amount ?? 0);
/** A USD estimate beside an amount that is not already in dollars. */
const showUsd = (amount: number | null, currency: string) =>
  amount != null && amount > 0 && currency.toUpperCase() !== 'USD';

const money = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

type PlannerV3ItineraryProps = {
  /** The trip's first day: dates each day for supplier-rate validity (XT-828). */
  startOn?: string | null;
  draft: AdvisorDraft;
  travellers: PlannerTravellers;
  isGenerating: boolean;
  generatingDayId: string | null;
  generateError: boolean;
  onGenerate: () => void;
  onGenerateDay: (legId: string, dayId: string) => void;
  /** Per-day Generate picks waiting to be accepted or skipped. */
  suggestions?: DaySuggestions;
  /** Product ids whose price, map point and duration are still loading. */
  pendingDetailIds?: ReadonlySet<string>;
  onAcceptSuggestion: (dayId: string, item: AdvisorDraftItem) => void;
  onSkipSuggestion: (dayId: string, item: AdvisorDraftItem) => void;
  onDismissSuggestions: (dayId: string) => void;
  onRemoveSuggested: () => void;
  onAddDay: (legId: string) => void;
  onRemoveLeg: (leg: AdvisorDraftLeg) => void;
  onRemoveItem: (item: AdvisorDraftItem) => void;
  /** Drops a day's Guide or Transport line (XT-828). */
  onRemoveService?: (dayId: string, service: PlannerDayService) => void;
  /** Sets the cars on a day's Transport; null: the suggested vehicle. */
  onTransportVehiclesChange?: (
    dayId: string,
    vehicles: PlannerTransportVehicle[] | null
  ) => void;
  /** Sets a day's Transport to a half or a full day (T-326). */
  onTransportSpanChange?: (dayId: string, span: PlannerServiceSpan) => void;
  /** Brings back a Guide or Transport the agent removed. */
  onRestoreService?: (dayId: string, service: RestorableService) => void;
  /** The trip's transfers (XT-920): drop one car (else the transfer), bring
   * one back, or set its cars, a flight or a train. */
  onTransferChange?: TransferChange<[dayId: string]>;
  /** Sets the agent's comment on an item for the DMC (XT-834). */
  onCommentChange?: (itemId: string, comment: string) => void;
  /** A base hotel's Send me a quote / Already booked (T-357). */
  onHotelBookingChange?: (itemId: string, booking: AdvisorHotelBooking) => void;
  /** Sets a day item's start and end; absent, times are shown only. */
  onTimeChange?: (itemId: string, startTime: string, endTime: string) => void;
  /** The travellers booked on a product; null for everyone (XT-840). */
  onGuestsChange?: (productId: string, guests: AdvisorGuests | null) => void;
  /** Removes a day with its items (`number` as the trip shows it). */
  onRemoveDay?: (day: AdvisorDraftDay, number: number) => void;
  onRemoveBaseHotel: (leg: AdvisorDraftLeg) => void;
  /** Shows hotels for the chapter's region to pick a base hotel from. */
  onFindHotels: (leg: AdvisorDraftLeg) => void;
  /** The country's regions: each chapter is one of them. */
  regions?: readonly AdvisorRegion[];
  /** Adds a chapter for a region the trip does not have yet. */
  onAddRegion?: (region: AdvisorRegion) => void;
  /** Filters the results to the chapter's region. */
  onShowRegion?: (leg: AdvisorDraftLeg) => void;
  /** The host's actions beside the estimated total. */
  footerActions?: ReactNode;
};

const FOCUS_CLEARANCE = 8;

const scrollParent = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
  }
  return null;
};

// The browser counts a control under the pinned total as on screen, so
// keyboard focus would land out of sight; scroll it out from under the bar.
const keepFocusClearOf =
  (bar: HTMLElement | null) => (event: FocusEvent<HTMLElement>) => {
    const target = event.target;
    if (!bar || bar.contains(target) || !target.matches(':focus-visible')) {
      return;
    }
    const hidden =
      bar.getBoundingClientRect().bottom - target.getBoundingClientRect().top;
    if (hidden > 0) {
      scrollParent(bar)?.scrollBy({ top: -(hidden + FOCUS_CLEARANCE) });
    }
  };

export const PlannerV3Itinerary = ({
  draft,
  travellers,
  isGenerating,
  generatingDayId,
  generateError,
  onGenerate,
  onGenerateDay,
  suggestions = {},
  pendingDetailIds = NONE_PENDING,
  onAcceptSuggestion,
  onSkipSuggestion,
  onDismissSuggestions,
  onRemoveSuggested,
  onAddDay,
  onRemoveLeg,
  onRemoveItem,
  onRemoveService,
  onTransportVehiclesChange,
  onTransportSpanChange,
  onRestoreService,
  onTransferChange,
  onCommentChange,
  onHotelBookingChange,
  onTimeChange,
  onGuestsChange,
  onRemoveDay,
  onRemoveBaseHotel,
  onFindHotels,
  regions = [],
  onAddRegion,
  onShowRegion,
  footerActions,
  startOn = null,
}: PlannerV3ItineraryProps) => {
  const inTrip = new Set(draft.legs.map((leg) => leg.regionId));
  const addable = regions.filter(
    (entry) => entry.parentId === null && !inTrip.has(entry.id)
  );
  const [alertsOpen, setAlertsOpen] = useState(false);
  const totalsRef = useRef<HTMLElement | null>(null);
  const totalsDrop = useDroppable({ id: ADVISOR_TOTALS_ID });
  const alerts = itineraryAlerts(draft, regionName);
  const host = useOptionalPlannerHost();
  // Hosts that show the XT-817 hotel average also estimate with it.
  const showAvgRoomNight = host?.showAvgRoomNight ?? false;
  const servicePricing = host?.useServicePricing?.() ?? NO_PRICING;
  // XT-145: the supplier's own cells, priced per vehicle.
  const transportRates = host?.useTransportRates?.() ?? NO_TRANSPORT_RATES;
  const pricing = host?.useCurrency?.() ?? null;
  const total = estimatedTotal(draft, travellers, {
    useAvgRoomNight: showAvgRoomNight,
    servicePricing,
    transportRates,
    currency: pricing?.currencyFor(draft.legs[0]?.country) ?? null,
    serviceCurrency: (country) => pricing?.currencyFor(country) ?? null,
    fx: pricing?.fx,
    startOn,
  });
  const commissionRates = usePlannerCommissionRates();
  const commission = estimateAgencyCommissionByCategory(
    total.byCategory,
    commissionRates
  );
  const earn = Math.round(commission);
  const earnUsd = toUsdEstimate(earn, total.currency, pricing?.fx);
  // T-405: where the quote carries the agency's commission (the portal), the
  // total does too, as the Midlayer shows it once the trip is sent.
  const totalAmount = host?.useCommissionRates
    ? total.amount + commission
    : total.amount;
  const totalUsd = host?.useCommissionRates
    ? toUsdEstimate(totalAmount, total.currency, pricing?.fx)
    : total.usd;
  // A Guide or Transport is in its chapter's member currency.
  const serviceCurrencyOf = (country: string | null | undefined) =>
    pricing?.currencyFor(country) ?? total.currency;
  const hasDays = draft.legs.some((leg) => leg.days.length > 0);
  const transfers = tripTransfers(draft, travellers, {
    servicePricing,
    transportRates,
    serviceCurrency: (country) => pricing?.currencyFor(country) ?? null,
    currency: total.currency,
    fx: pricing?.fx,
    startOn,
  });
  let dayNumber = 0;
  const checkOuts = hotelCheckOuts(draft);

  return (
    <section
      aria-label="Itinerary"
      className={css.root}
      onFocus={(event) => keepFocusClearOf(totalsRef.current)(event)}
    >
      <div className={css.head}>
        <h2 className={css.title}>Itinerary</h2>
        <span className={css.summary}>{itinerarySummary(draft)}</span>
        <div className={css.headActions}>
          {draftHasSuggested(draft) ? (
            <button
              className={css.link}
              onClick={onRemoveSuggested}
              type="button"
            >
              Remove suggested
            </button>
          ) : null}
          {hasDays ? null : (
            <button
              className={css.dark}
              disabled={isGenerating}
              onClick={onGenerate}
              type="button"
            >
              {isGenerating ? 'Generating…' : '✦ Generate'}
            </button>
          )}
        </div>
      </div>
      <section
        aria-label="Trip total"
        className={css.totals}
        ref={(node) => {
          totalsRef.current = node;
          totalsDrop.setNodeRef(node);
        }}
      >
        <div className={css.estimate}>
          <span className={css.totalLabel}>Estimated total</span>
          <p className={css.total}>
            from {money(totalAmount, total.currency)}
            {showUsd(totalUsd, total.currency) ? (
              <span className={css.usd}> (est. {usd(totalUsd)})</span>
            ) : null}
          </p>
          {total.alreadyBooked > 0 ? (
            <span className={css.bookedNote} role="note">
              <Info aria-hidden size={12} />
              {ALREADY_BOOKED_NOTE}
            </span>
          ) : null}
          <span className={css.note}>{estimateNote(total, travellers)}</span>
          <span className={css.earn}>
            You earn <strong>{money(earn, total.currency)}</strong>
            {showUsd(earnUsd, total.currency) ? (
              <span className={css.usd}> (est. {usd(earnUsd)})</span>
            ) : null}
          </span>
        </div>
        {footerActions ? (
          <div className={css.totalsActions}>{footerActions}</div>
        ) : null}
      </section>
      {generateError ? (
        <p className={css.note} role="alert">
          Could not generate a draft.
        </p>
      ) : null}

      {draft.legs.length === 0 ? null : alerts.length === 0 ? (
        <p className={css.ready}>✓ No issues — ready for review</p>
      ) : (
        <div className={css.alerts}>
          <button
            aria-expanded={alertsOpen}
            className={css.alertsToggle}
            onClick={() => setAlertsOpen((open) => !open)}
            type="button"
          >
            <span className={css.bubble}>{alerts.length}</span>
            to fix before review
            <span aria-hidden="true" className={css.chevron}>
              {alertsOpen ? '▲' : '▼'}
            </span>
          </button>
          {alertsOpen ? (
            <ul className={css.alertList}>
              {alerts.map((alert) => (
                <li key={`${alert.kind}-${alert.text}`}>{alert.text}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {draft.legs.length === 0 ? <EmptyRail /> : null}

      {draft.legs.map((leg) => {
        const region = regionName(leg.regionTitle);
        return (
          <section aria-label={region} className={css.region} key={leg.id}>
            <div className={css.regionHead}>
              <h3 className={css.regionTitle}>
                {onShowRegion &&
                regions.some((entry) => entry.id === leg.regionId) ? (
                  <button
                    aria-label={`Show results in ${region}`}
                    className={css.regionButton}
                    onClick={() => onShowRegion(leg)}
                    type="button"
                  >
                    {region}
                  </button>
                ) : (
                  region
                )}
              </h3>
              <span className={css.regionDays}>
                {leg.days.length} {leg.days.length === 1 ? 'day' : 'days'}
              </span>
              <button
                className={linkCss({ isDanger: true })}
                onClick={() => onRemoveLeg(leg)}
                type="button"
              >
                Remove
              </button>
            </div>
            {leg.baseHotel ? (
              <BaseHotelCard
                hotel={leg.baseHotel}
                isLoading={pendingDetailIds.has(leg.baseHotel.productId)}
                nights={chapterNights(leg)}
                onCommentChange={onCommentChange}
                onHotelBookingChange={
                  host?.askHotelBooking ? onHotelBookingChange : undefined
                }
                onRemove={() => onRemoveBaseHotel(leg)}
                region={region}
                showAvgRoomNight={showAvgRoomNight}
              />
            ) : (
              <button
                className={css.noHotel}
                onClick={() => onFindHotels(leg)}
                type="button"
              >
                <strong>! No base hotel in this region</strong>
                <span>
                  Distances can’t be calculated. Tap to filter hotels here.
                </span>
              </button>
            )}
            {leg.days.map((day) => {
              dayNumber += 1;
              const number = dayNumber;
              return (
                <DayBlock
                  checkOuts={checkOuts.get(day.id) ?? []}
                  day={day}
                  isBusy={isGenerating && generatingDayId === day.id}
                  isGenerating={isGenerating}
                  key={day.id}
                  leg={leg}
                  number={number}
                  onAcceptSuggestion={(item) =>
                    onAcceptSuggestion(day.id, item)
                  }
                  onDismissSuggestions={() => onDismissSuggestions(day.id)}
                  onGenerate={() => onGenerateDay(leg.id, day.id)}
                  onRemove={
                    // A chapter keeps its last night (Remove drops the
                    // region); the departure day always stays.
                    onRemoveDay &&
                    !isDepartureDay(day) &&
                    nightDays(leg).length > 1
                      ? () => onRemoveDay(day, number)
                      : undefined
                  }
                  onRemoveItem={onRemoveItem}
                  onCommentChange={onCommentChange}
                  onTimeChange={onTimeChange}
                  onGuestsChange={onGuestsChange}
                  onRemoveService={
                    onRemoveService
                      ? (service) => onRemoveService(day.id, service)
                      : undefined
                  }
                  onTransportVehiclesChange={
                    onTransportVehiclesChange
                      ? (vehicles) =>
                          onTransportVehiclesChange(day.id, vehicles)
                      : undefined
                  }
                  onTransportSpanChange={
                    onTransportSpanChange
                      ? (span) => onTransportSpanChange(day.id, span)
                      : undefined
                  }
                  onRestoreService={
                    onRestoreService
                      ? (service) => onRestoreService(day.id, service)
                      : undefined
                  }
                  transfers={transfers.filter(
                    (transfer) => transfer.dayId === day.id
                  )}
                  onTransferChange={
                    onTransferChange
                      ? {
                          remove: (kind, vehicle) =>
                            onTransferChange.remove(day.id, kind, vehicle),
                          restore: (kind) =>
                            onTransferChange.restore(day.id, kind),
                          vehicles: (kind, vehicles) =>
                            onTransferChange.vehicles(day.id, kind, vehicles),
                          place: (kind, place) =>
                            onTransferChange.place(day.id, kind, place),
                        }
                      : undefined
                  }
                  rates={plannerServiceRates(
                    servicePricing,
                    leg.country,
                    transportRates,
                    {
                      regionId: leg.regionId,
                      currency: serviceCurrencyOf(leg.country),
                      fx: pricing?.fx,
                    }
                  )}
                  serviceCurrency={serviceCurrencyOf(leg.country)}
                  startOn={startOn}
                  party={travellers}
                  onSkipSuggestion={(item) => onSkipSuggestion(day.id, item)}
                  suggestions={suggestions[day.id]}
                  pendingDetailIds={pendingDetailIds}
                />
              );
            })}
            <button
              className={`${css.link} ${css.addDay}`}
              onClick={() => onAddDay(leg.id)}
              type="button"
            >
              + Add day
            </button>
          </section>
        );
      })}

      {onAddRegion && addable.length > 0 ? (
        <select
          aria-label="Add region"
          className={css.addRegion}
          onChange={(event) => {
            const picked = addable.find(
              (entry) => entry.id === event.target.value
            );
            if (picked) onAddRegion(picked);
          }}
          value=""
        >
          <option value="">+ Add region</option>
          {addable.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {regionName(entry.title)}
            </option>
          ))}
        </select>
      ) : null}
    </section>
  );
};

const BaseHotelCard = ({
  hotel,
  isLoading,
  region,
  nights,
  showAvgRoomNight = false,
  onRemove,
  onCommentChange,
  onHotelBookingChange,
}: {
  hotel: AdvisorDraftItem;
  isLoading: boolean;
  region: string;
  nights: number;
  /** Host capability: a hotel on request shows its historical average. */
  showAvgRoomNight?: boolean;
  onRemove: () => void;
  onCommentChange?: (itemId: string, comment: string) => void;
  onHotelBookingChange?: (itemId: string, booking: AdvisorHotelBooking) => void;
}) => {
  // T-405: the portal's prices carry the agency's commission on top.
  const priced = useCommissionOnTop().item(hotel);
  const price = priceLabel(
    priced.clientPrice,
    priced.currency,
    priced.pricingType
  );
  const onRequest = priced.clientPrice == null || priced.clientPrice <= 0;
  const average =
    showAvgRoomNight && onRequest && usableAvgRoomNight(priced.avgRoomNight)
      ? priced.avgRoomNight
      : null;
  return (
    <section
      aria-label={`Base hotel · ${hotel.title}`}
      className={css.baseHotel}
    >
      {hotel.thumbnail ? (
        <img
          alt=""
          className={css.thumb}
          loading="lazy"
          src={hotel.thumbnail}
        />
      ) : (
        <span aria-hidden="true" className={css.thumb} />
      )}
      <span className={css.itemText}>
        <span className={css.baseCaption}>Base hotel</span>
        <span className={css.itemName}>{hotel.title}</span>
        <span className={css.itemMeta}>
          {`${region} · `}
          {isLoading ? (
            'Loading details…'
          ) : average ? (
            <>
              <AvgRoomNightPrice avgRoomNight={average} />
              {/* The average is in EUR. */}
              <UsdEstimate
                amount={usableAvgRoomNight(average)}
                currency="EUR"
              />
            </>
          ) : (
            <>
              <PriceText price={price} />
              <UsdEstimate
                amount={priced.clientPrice}
                currency={priced.currency}
              />
            </>
          )}
          {` · ${nights} ${nights === 1 ? 'night' : 'nights'}`}
        </span>
      </span>
      <button
        aria-label={`Remove base hotel ${hotel.title}`}
        className={css.remove}
        onClick={onRemove}
        type="button"
      >
        ✕
      </button>
      {onHotelBookingChange ? (
        <HotelBookingChoice hotel={hotel} onChange={onHotelBookingChange} />
      ) : null}
      {onCommentChange ? (
        <ItemComment item={hotel} onChange={onCommentChange} />
      ) : null}
    </section>
  );
};

const ALREADY_BOOKED_NOTE =
  'Already booked hotels are not included in the total price.';

const HOTEL_BOOKING_LABELS: Record<AdvisorHotelBooking, string> = {
  quote: 'Send me a quote',
  booked: 'Already booked',
};

/** T-357: whether the DMC quotes the base hotel or the agent booked it. */
const HotelBookingChoice = ({
  hotel,
  onChange,
}: {
  hotel: AdvisorDraftItem;
  onChange: (itemId: string, booking: AdvisorHotelBooking) => void;
}) => (
  <fieldset className={css.hotelBooking}>
    <legend className={css.hotelBookingLegend}>
      {`Hotel booking for ${hotel.title}`}
    </legend>
    {advisorHotelBookings.map((booking) => (
      <label className={css.hotelBookingOption} key={booking}>
        <input
          checked={hotel.hotelBooking === booking}
          name={`hotel-booking-${hotel.id}`}
          onChange={() => onChange(hotel.id, booking)}
          type="radio"
          value={booking}
        />
        {HOTEL_BOOKING_LABELS[booking]}
      </label>
    ))}
  </fieldset>
);

/**
 * The agent's comment for the DMC on an item (XT-834): "Add comment", or the
 * comment with Edit; it opens the day's comments once the request is sent.
 */
const ItemComment = ({
  item,
  onChange,
}: {
  item: AdvisorDraftItem;
  onChange: (itemId: string, comment: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.comment ?? '');
  if (editing) {
    return (
      <div className={css.comment}>
        <textarea
          aria-label={`Comment on ${item.title}`}
          className={css.commentInput}
          maxLength={2000}
          onChange={(event) => setText(event.target.value)}
          placeholder="A note for the DMC about this item…"
          rows={2}
          value={text}
        />
        <div className={css.commentActions}>
          <button
            className={css.link}
            onClick={() => {
              setText(item.comment ?? '');
              setEditing(false);
            }}
            type="button"
          >
            Cancel
          </button>
          <button
            className={css.commentSave}
            onClick={() => {
              onChange(item.id, text);
              setEditing(false);
            }}
            type="button"
          >
            Save
          </button>
        </div>
      </div>
    );
  }
  if (item.comment) {
    return (
      <div className={css.comment}>
        <p className={css.commentText}>
          <MessageSquare aria-hidden="true" size={12} />
          <span>{item.comment}</span>
        </p>
        <button
          aria-label={`Edit comment on ${item.title}`}
          className={css.link}
          onClick={() => {
            setText(item.comment ?? '');
            setEditing(true);
          }}
          type="button"
        >
          Edit
        </button>
      </div>
    );
  }
  return (
    <div className={css.comment}>
      <button
        aria-label={`Add comment on ${item.title}`}
        className={`${css.link} ${css.commentAdd}`}
        onClick={() => setEditing(true)}
        type="button"
      >
        <MessageSquare aria-hidden="true" size={12} /> Add comment
      </button>
    </div>
  );
};

const EmptyRail = () => {
  const drop = useDroppable({ id: ADVISOR_RAIL_DROP_ID });
  return (
    <div className={emptyCss({ isOver: drop.isOver })} ref={drop.setNodeRef}>
      Add a hotel or experience to start the itinerary.
    </div>
  );
};

type DayBlockProps = {
  day: AdvisorDraftDay;
  leg: AdvisorDraftLeg;
  number: number;
  isBusy: boolean;
  isGenerating: boolean;
  suggestions: AdvisorDraftItem[] | undefined;
  pendingDetailIds: ReadonlySet<string>;
  onGenerate: () => void;
  onRemove?: () => void;
  onRemoveItem: (item: AdvisorDraftItem) => void;
  onRemoveService?: (service: PlannerDayService) => void;
  onTransportVehiclesChange?: (
    vehicles: PlannerTransportVehicle[] | null
  ) => void;
  onTransportSpanChange?: (span: PlannerServiceSpan) => void;
  onRestoreService?: (service: RestorableService) => void;
  /** The trip's transfers that run on this day (XT-920). */
  transfers: readonly PricedTransfer[];
  onTransferChange?: TransferChange<[]>;
  onCommentChange?: (itemId: string, comment: string) => void;
  onTimeChange?: (itemId: string, startTime: string, endTime: string) => void;
  onGuestsChange?: (productId: string, guests: AdvisorGuests | null) => void;
  /** The chapter country's Guide and Transport prices. */
  rates: PlannerServiceRates;
  /** The currency those prices are in (the chapter's member's). */
  serviceCurrency: string;
  /** The trip's first day, for the day's date (XT-828). */
  startOn: string | null;
  /** Base hotels whose stay ends this day (read-only). */
  checkOuts: AdvisorDraftItem[];
  /** The brief's party (XT-840: the day's vehicle seats its largest group). */
  party: PlannerTravellers;
  onAcceptSuggestion: (item: AdvisorDraftItem) => void;
  onSkipSuggestion: (item: AdvisorDraftItem) => void;
  onDismissSuggestions: () => void;
};

const DayBlock = ({
  day,
  leg,
  number,
  isBusy,
  isGenerating,
  suggestions,
  pendingDetailIds,
  onGenerate,
  onRemove,
  onRemoveItem,
  onRemoveService,
  onTransportVehiclesChange,
  onTransportSpanChange,
  onRestoreService,
  transfers,
  onTransferChange,
  onCommentChange,
  onTimeChange,
  onGuestsChange,
  rates,
  serviceCurrency,
  startOn,
  checkOuts,
  party,
  onAcceptSuggestion,
  onSkipSuggestion,
  onDismissSuggestions,
}: DayBlockProps) => {
  const services = dayServices(day);
  const partyGuests = { adults: party.adults, children: party.children };
  // The car carries the day's largest group to and from the hotel.
  const group = plannerDayGroupSize(day.items, partyGuests);
  // The party, the day's date and a lone experience's route price its lines.
  const serviceDay = dayServiceContext(
    leg,
    day,
    number - 1,
    startOn,
    partyGuests
  );
  // With two or more experiences the Transport is the day's own item, as XT
  // books it; a lone experience keeps it as its subitem. One line per car.
  const dayTransports = services.filter(
    (service) => service.kind === 'transport' && service.span
  );
  const [vehiclesOpen, setVehiclesOpen] = useState(false);
  // A removed Transport, or one no experience needs, can be added on the
  // experience it would serve: the first that needs a car, else the first.
  const tours = day.items.filter((item) => item.kind === 'experience');
  const transportTourId = (tours.find(tourNeedsCar) ?? tours[0])?.id;
  const noTransport = !services.some((service) => service.kind === 'transport');
  const fleet = plannerTransportSeats(day.transportVehicles, group);
  const onEditVehicles = onTransportVehiclesChange
    ? () => setVehiclesOpen((open) => !open)
    : undefined;
  const vehicleEditor =
    vehiclesOpen && onTransportVehiclesChange ? (
      <TransportVehicles
        group={group}
        onChange={onTransportVehiclesChange}
        onClose={() => setVehiclesOpen(false)}
        vehicles={day.transportVehicles}
      />
    ) : null;
  // Something from another region is being dragged: this day fades, and a
  // drop on it is refused with the reason (TAP01-034).
  const dragged = useDndContext().active?.data.current as
    | { regionId?: string | null }
    | undefined;
  const blocked = Boolean(
    dragged?.regionId && dragged.regionId !== leg.regionId
  );
  // Something that fits is being dragged: every day that takes it shows so.
  const isTarget = Boolean(dragged) && !blocked;
  const drop = useDroppable({
    id: advisorDayDropId(day.id),
    data: { kind: 'advisor-day', dayId: day.id },
  });
  const minutes = dayMinutes(day);
  const over = minutes > DAY_LIMIT_MINUTES;
  const dayDate = plannerDayDateLabel(startOn, number - 1);
  const clocks = dayItemClocks(day);
  const items = itemsByClock(day, clocks);
  // The arrival and a move between chapters open the day; the departure
  // closes it.
  const transferRow = (transfer: PricedTransfer) => (
    <TransferRows
      key={transfer.key}
      onChange={onTransferChange}
      transfer={transfer}
    />
  );
  return (
    <section
      aria-busy={isBusy}
      aria-label={`Day ${number}`}
      className={dayCss({
        isOver: drop.isOver && !blocked,
        isBlocked: blocked,
        isTarget: isTarget && !drop.isOver,
      })}
      ref={drop.setNodeRef}
    >
      <div className={css.dayHead}>
        <span className={css.dayTitle}>
          Day {number}
          {dayDate ? <span className={css.dayDate}> · {dayDate}</span> : null}
          {isDepartureDay(day) ? (
            <span className={css.dayDate}> · Departure</span>
          ) : null}
        </span>
        <span className={hoursCss({ isOver: over })}>
          {formatHours(minutes)}
        </span>
        <button
          aria-label={`Generate day ${number}`}
          className={css.generate}
          disabled={isGenerating}
          onClick={onGenerate}
          type="button"
        >
          {isBusy ? 'Generating…' : '✦ Generate'}
        </button>
        {onRemove ? (
          <button
            aria-label={`Remove day ${number}`}
            className={css.remove}
            onClick={onRemove}
            title="Remove day"
            type="button"
          >
            ✕
          </button>
        ) : null}
      </div>
      {checkOuts.map((hotel) => (
        <div className={css.checkOut} key={`check-out:${hotel.id}`}>
          <span className={css.checkOutLabel}>Check-out</span>
          <span className={css.serviceLabel}>{hotel.title}</span>
        </div>
      ))}
      {transfers
        .filter((transfer) => transfer.kind !== 'departure')
        .map(transferRow)}
      {dayTransports.map((transport) => (
        <DayServiceRow
          key={transport.unit ?? 0}
          onRemove={
            onRemoveService ? () => onRemoveService(transport) : undefined
          }
          onEditVehicles={onEditVehicles}
          onChangeSpan={onTransportSpanChange}
          currency={serviceCurrency}
          price={
            plannerServiceRate(transport, rates, serviceDay(transport)).price
          }
          service={transport}
          group={group}
          fleet={fleet}
        />
      ))}
      {dayTransports.length > 0 ? vehicleEditor : null}
      <SortableContext
        items={items.map((item) => advisorItemDragId(item.id))}
        strategy={verticalListSortingStrategy}
      >
        {items.length > 0 ? (
          <ul className={css.items}>
            {items.map((item) => (
              <ItemRow
                item={item}
                key={item.id}
                leg={leg}
                party={partyGuests}
                clock={clocks.get(item.id)}
                onTimeChange={onTimeChange}
                onGuestsChange={onGuestsChange}
                meta={
                  pendingDetailIds.has(item.productId)
                    ? 'Loading details…'
                    : itemMeta(leg, item, partyGuests)
                }
                group={group}
                fleet={fleet}
                serviceDay={serviceDay}
                onRemove={() => onRemoveItem(item)}
                onRemoveService={onRemoveService}
                onEditVehicles={onEditVehicles}
                vehicleEditor={dayTransports.length > 0 ? null : vehicleEditor}
                restorable={
                  onRestoreService
                    ? removedServices(item, {
                        transportMissing:
                          noTransport && item.id === transportTourId,
                      })
                    : []
                }
                onRestoreService={onRestoreService}
                onCommentChange={onCommentChange}
                rates={rates}
                serviceCurrency={serviceCurrency}
                services={services.filter(
                  (service) =>
                    service.itemId === item.id &&
                    !dayTransports.includes(service)
                )}
              />
            ))}
          </ul>
        ) : null}
      </SortableContext>
      {transfers
        .filter((transfer) => transfer.kind === 'departure')
        .map(transferRow)}
      {day.items.length === 0 && !isBusy && !suggestions ? (
        <div className={css.dropHint}>Drop an item here or Generate</div>
      ) : null}
      {isBusy ? (
        <>
          <div className={css.skeleton} />
          <div className={css.skeleton} />
        </>
      ) : suggestions ? (
        <SuggestionPanel
          dayNumber={number}
          pendingIds={pendingDetailIds}
          items={suggestions}
          leg={leg}
          onAccept={onAcceptSuggestion}
          onDismiss={onDismissSuggestions}
          onSkip={onSkipSuggestion}
        />
      ) : null}
    </section>
  );
};

/** The day's Generate picks, each to accept onto the day or skip. */
const SuggestionPanel = ({
  dayNumber,
  pendingIds,
  items,
  leg,
  onAccept,
  onDismiss,
  onSkip,
}: {
  dayNumber: number;
  pendingIds: ReadonlySet<string>;
  items: AdvisorDraftItem[];
  leg: AdvisorDraftLeg;
  onAccept: (item: AdvisorDraftItem) => void;
  onDismiss: () => void;
  onSkip: (item: AdvisorDraftItem) => void;
}) => (
  <section
    aria-label={`Suggestions for day ${dayNumber}`}
    className={css.suggestions}
  >
    <p className={css.suggestionsHead}>
      ✦ Suggestions
      <span className={css.suggestionsWhy}>· picked from similar trips</span>
    </p>
    {items.length === 0 ? (
      <p className={css.suggestionsNone}>
        Nothing new to suggest for this day.
        <button className={css.link} onClick={onDismiss} type="button">
          Dismiss
        </button>
      </p>
    ) : (
      <ul className={css.suggestionList}>
        {items.map((item) => (
          <li aria-label={item.title} className={css.suggestion} key={item.id}>
            {item.thumbnail ? (
              <img
                alt=""
                className={css.miniThumb}
                loading="lazy"
                src={item.thumbnail}
              />
            ) : (
              <span aria-hidden="true" className={css.miniThumb} />
            )}
            <span className={css.itemText}>
              <span className={css.itemName}>{item.title}</span>
              <span className={css.itemMeta}>
                {item.startTime ? `${item.startTime} · ` : null}
                {pendingIds.has(item.productId)
                  ? 'Loading details…'
                  : itemMeta(leg, item)}
              </span>
            </span>
            <span className={css.suggestionActions}>
              <button
                aria-label={`Skip ${item.title}`}
                className={css.skip}
                onClick={() => onSkip(item)}
                type="button"
              >
                Skip
              </button>
              <button
                aria-label={`Accept ${item.title}`}
                className={css.accept}
                onClick={() => onAccept(item)}
                type="button"
              >
                Accept
              </button>
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

/** An item's price as the portal shows it: commission on top (T-405). */
const ItemPrice = ({ item }: { item: AdvisorDraftItem }) => {
  const priced = useCommissionOnTop().item(item);
  return (
    <>
      <PriceText
        price={priceLabel(
          priced.clientPrice,
          priced.currency,
          priced.pricingType
        )}
      />
      <UsdEstimate amount={priced.clientPrice} currency={priced.currency} />
    </>
  );
};

/** A service line's price, with its category's commission on top (T-405). */
const LinePrice = ({
  amount,
  currency,
  category,
}: {
  amount: number;
  currency: string;
  category: CommissionCategory;
}) => {
  const shown = useCommissionOnTop().price(amount, category);
  return (
    <>
      {money(shown, currency)}
      <UsdEstimate amount={shown} currency={currency} />
    </>
  );
};

/** "3h · €380 / pp · 27 km · 4 of 6 guests" — what the row can tell. */
const itemMeta = (
  leg: AdvisorDraftLeg,
  item: AdvisorDraftItem,
  party?: { adults: number; children: number }
): ReactNode => {
  const booked = party ? guestsLabel(item.guests, party) : null;
  const hotel = item.kind === 'hotel' ? null : chapterHotelPoint(leg);
  const duration = formatDuration(item.kind, item.durationMinutes);
  const km =
    hotel && item.location ? formatKm(haversineKm(item.location, hotel)) : null;
  return (
    <>
      {duration === '—' ? null : `${duration} · `}
      <ItemPrice item={item} />
      {km ? ` · ${km}` : null}
      {booked ? ` · ${booked}` : null}
    </>
  );
};

/**
 * "Transport · full day · Minivan 👤 × 7": the vehicle that seats the day's
 * largest group, red when none does.
 */
const ServiceName = ({
  service,
  group,
  fleet,
  onEditVehicles,
  onChangeSpan,
}: {
  service: PlannerDayService;
  group: number;
  /** All the day's cars' seats, and whether they seat its group. */
  fleet?: { seats: number; fits: boolean };
  /** Opens the day's vehicle picker. */
  onEditVehicles?: () => void;
  /** Switches a day's Transport between a half and a full day. */
  onChangeSpan?: (span: PlannerServiceSpan) => void;
}) => {
  const label = plannerServiceLabel(service);
  if (service.kind !== 'transport') {
    return <span className={css.serviceName}>{label}</span>;
  }
  const suggested = plannerVehicle(group);
  const vehicle = service.vehicle
    ? (plannerVehicleOf(service.vehicle) ?? suggested)
    : suggested;
  const fits = fleet?.fits ?? suggested.fits;
  return (
    <span className={css.serviceName}>
      <span className={css.serviceLabel}>
        {service.span && onChangeSpan ? (
          <>
            Transport ·{' '}
            <button
              aria-label={`Change to ${service.span === 'half' ? 'full' : 'half'} day`}
              className={css.vehicleButton}
              onClick={() =>
                onChangeSpan(service.span === 'half' ? 'full' : 'half')
              }
              title={`Make it a ${service.span === 'half' ? 'full' : 'half'} day`}
              type="button"
            >
              {service.span === 'half' ? 'half day' : 'full day'}
            </button>
          </>
        ) : (
          label
        )}{' '}
        ·{' '}
        {onEditVehicles ? (
          <button
            aria-label={`Change vehicles · ${vehicle.type}`}
            className={css.vehicleButton}
            onClick={onEditVehicles}
            title="Change the vehicle or add more"
            type="button"
          >
            {vehicle.type}
          </button>
        ) : (
          vehicle.type
        )}
      </span>
      <span
        className={fits ? css.seats : css.seatsOver}
        title={
          fits
            ? `Fits ${group} ${group === 1 ? 'guest' : 'guests'} · ${vehicle.type.toLowerCase()}, ${vehicle.seats} seats`
            : `${group} guests, ${fleet?.seats ?? vehicle.seats} seats`
        }
      >
        <User aria-hidden="true" size={11} />× {vehicle.seats}
      </span>
    </span>
  );
};

export type RestorableService = {
  kind: 'guide' | 'transport';
  itemId: string;
};

/** The Guide / Transport an experience can add: removed, or not suggested. */
const removedServices = (
  item: AdvisorDraftItem,
  day: { transportMissing: boolean }
): RestorableService[] => {
  if (item.kind !== 'experience') return [];
  const removed: RestorableService[] = [];
  if (day.transportMissing)
    removed.push({ kind: 'transport', itemId: item.id });
  if (item.guideRemoved) removed.push({ kind: 'guide', itemId: item.id });
  return removed;
};

/** "Remove Transport · full day", "… · Sedan 2" when one of several cars. */
const removeLabel = (service: PlannerDayService): string =>
  service.vehicle
    ? `Remove ${plannerServiceLabel(service)} · ${service.vehicle} ${(service.unit ?? 0) + 1}`
    : `Remove ${plannerServiceLabel(service)}`;

/** A day's half- or full-day Transport, its own line like an item. */
const DayServiceRow = ({
  service,
  price,
  currency,
  group,
  fleet,
  onRemove,
  onEditVehicles,
  onChangeSpan,
}: {
  service: PlannerDayService;
  price: number;
  currency: string;
  group: number;
  fleet?: { seats: number; fits: boolean };
  onRemove?: () => void;
  onEditVehicles?: () => void;
  onChangeSpan?: (span: PlannerServiceSpan) => void;
}) => {
  const label = plannerServiceLabel(service);
  return (
    <section aria-label={label} className={css.dayService}>
      <ServiceName
        group={group}
        onEditVehicles={onEditVehicles}
        onChangeSpan={onChangeSpan}
        fleet={fleet}
        service={service}
      />
      <span className={css.servicePrice}>
        <LinePrice
          amount={price}
          category={commissionCategoryOfDayService(service)}
          currency={currency}
        />
      </span>
      {onRemove ? (
        <button
          aria-label={removeLabel(service)}
          className={css.remove}
          onClick={onRemove}
          type="button"
        >
          ✕
        </button>
      ) : null}
    </section>
  );
};

/** What a transfer's rows can change, keyed first by `Scope` (its day). */
export type TransferChange<Scope extends unknown[]> = {
  remove: (
    ...args: [...Scope, kind: PlannerTransferKind, vehicle?: string]
  ) => void;
  restore: (...args: [...Scope, kind: PlannerTransferKind]) => void;
  vehicles: (
    ...args: [
      ...Scope,
      kind: PlannerTransferKind,
      vehicles: PlannerTransportVehicle[] | null,
    ]
  ) => void;
  place: (
    ...args: [
      ...Scope,
      kind: 'arrival' | 'departure',
      place: string | PlannerTransferSpot | null,
    ]
  ) => void;
};

/**
 * A transfer's rows: "Arrival transfer · Minivan 👤 × 6" over its route
 * "Airport → Lisboa", one per car (or its flight or train, on request), each with its
 * price and ✕; the vehicle opens the picker. Once removed, a button to add it
 * back.
 */
const TransferRows = ({
  transfer,
  onChange,
}: {
  transfer: PricedTransfer;
  onChange?: TransferChange<[]>;
}) => {
  const [vehiclesOpen, setVehiclesOpen] = useState(false);
  const [placeOpen, setPlaceOpen] = useState(false);
  const label = plannerTransferLabel(transfer.kind);
  const { kind, place } = transfer;
  // The arrival's start or the departure's end: the agent can say where.
  const placeButton =
    onChange && place != null ? (
      <button
        aria-label={`Change where the ${label.toLowerCase()} ${kind === 'arrival' ? 'starts' : 'ends'} · ${place}`}
        className={css.vehicleButton}
        onClick={() => setPlaceOpen((open) => !open)}
        title="Airport, train station, port or another place"
        type="button"
      >
        {place}
      </button>
    ) : null;
  const route = (
    <>
      {kind === 'arrival' && placeButton ? placeButton : transfer.fromLabel}
      {' → '}
      {kind === 'departure' && placeButton ? placeButton : transfer.toLabel}
    </>
  );
  if (transfer.removed) {
    return onChange ? (
      <div className={css.restore}>
        <button
          aria-label={`Add ${label} back`}
          className={css.restoreButton}
          onClick={() => onChange.restore(transfer.kind)}
          type="button"
        >
          + {label}
        </button>
      </div>
    ) : null;
  }
  const several = transfer.lines.length > 1;
  return (
    <>
      {transfer.lines.map((line, index) => (
        <section aria-label={label} className={css.dayService} key={line.key}>
          <span className={css.transferText}>
            <span className={css.serviceName}>
              <span className={css.serviceLabel}>
                {label} ·{' '}
                {onChange ? (
                  <button
                    aria-label={`Change ${label} vehicles · ${line.vehicle}`}
                    className={css.vehicleButton}
                    onClick={() => setVehiclesOpen((open) => !open)}
                    title="Change the vehicle, add more, or fly"
                    type="button"
                  >
                    {line.vehicle}
                  </button>
                ) : (
                  line.vehicle
                )}
              </span>
              {line.seats == null ? null : (
                <span
                  className={transfer.fits ? css.seats : css.seatsOver}
                  title={`${line.vehicle.toLowerCase()}, ${line.seats} seats`}
                >
                  <User aria-hidden="true" size={11} />× {line.seats}
                </span>
              )}
            </span>
            <span className={css.transferRoute}>{route}</span>
          </span>
          <span className={css.servicePrice}>
            {line.price == null ? (
              'On request'
            ) : (
              <LinePrice
                amount={line.price}
                category="Transportation"
                currency={transfer.currency}
              />
            )}
          </span>
          {onChange ? (
            <button
              aria-label={
                several
                  ? `Remove ${label} · ${line.vehicle} ${index + 1}`
                  : `Remove ${label}`
              }
              className={css.remove}
              onClick={() =>
                onChange.remove(
                  transfer.kind,
                  line.picked ? line.vehicle : undefined
                )
              }
              type="button"
            >
              ✕
            </button>
          ) : null}
        </section>
      ))}
      {placeOpen && onChange && place != null && kind !== 'between' ? (
        <TransferPlace
          label={`${label} ${kind === 'arrival' ? 'start' : 'end'}`}
          onChange={(next) => onChange.place(kind, next)}
          onClose={() => setPlaceOpen(false)}
          place={place}
          spot={transfer.spot}
        />
      ) : null}
      {vehiclesOpen && onChange ? (
        <TransportVehicles
          allowOnRequest
          group={transfer.pax}
          label={`${label} vehicles`}
          onChange={(vehicles) => onChange.vehicles(transfer.kind, vehicles)}
          onClose={() => setVehiclesOpen(false)}
          vehicles={transfer.vehicles}
        />
      ) : null}
    </>
  );
};

const ItemRow = ({
  item,
  meta,
  leg,
  party,
  clock,
  onTimeChange,
  onGuestsChange,
  onRemove,
  services = [],
  rates,
  serviceCurrency = 'EUR',
  onRemoveService,
  onCommentChange,
  group = 1,
  fleet,
  serviceDay,
  onEditVehicles,
  vehicleEditor,
  restorable = [],
  onRestoreService,
}: {
  item: AdvisorDraftItem;
  meta: ReactNode;
  /** The day's largest group, for the Transport's vehicle. */
  group?: number;
  fleet?: { seats: number; fits: boolean };
  /** Opens the day's vehicle picker from its Transport line. */
  onEditVehicles?: () => void;
  /** The open vehicle picker, under this experience's Transport. */
  vehicleEditor?: ReactNode;
  /** Lines the agent removed from this experience, to bring back. */
  restorable?: readonly RestorableService[];
  onRestoreService?: (service: RestorableService) => void;
  /** What the day's lines are priced for (XT-828). */
  serviceDay?: ReturnType<typeof dayServiceContext>;
  /** The chapter it is in; it moves only within its region. */
  leg: AdvisorDraftLeg;
  /** The brief's party, for the opened item's price. */
  party: AdvisorGuests;
  /** When it runs; absent for a hotel. */
  clock?: ItemClock;
  onTimeChange?: (itemId: string, startTime: string, endTime: string) => void;
  onGuestsChange?: (productId: string, guests: AdvisorGuests | null) => void;
  onRemove: () => void;
  /** The Guide and Transport this experience adds (XT-828). */
  services?: readonly PlannerDayService[];
  rates?: PlannerServiceRates;
  serviceCurrency?: string;
  onRemoveService?: (service: PlannerDayService) => void;
  onCommentChange?: (itemId: string, comment: string) => void;
}) => {
  const sortable = useSortable({
    id: advisorItemDragId(item.id),
    data: { kind: 'advisor-item', itemId: item.id, regionId: leg.regionId },
  });
  const [timeOpen, setTimeOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <li
      aria-label={item.title}
      className={itemCss({ isDragging: sortable.isDragging })}
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
    >
      <button
        aria-label={`Move ${item.title}`}
        className={css.handle}
        type="button"
        {...sortable.attributes}
        {...sortable.listeners}
      >
        <GripVertical aria-hidden="true" size={14} />
      </button>
      <button
        aria-label={`${detailsOpen ? 'Close' : 'Open'} ${item.title}`}
        aria-expanded={detailsOpen}
        className={css.thumbButton}
        onClick={() => setDetailsOpen((open) => !open)}
        tabIndex={-1}
        type="button"
      >
        {item.thumbnail ? (
          <img
            alt=""
            className={css.thumb}
            loading="lazy"
            src={item.thumbnail}
          />
        ) : (
          <span aria-hidden="true" className={css.thumb} />
        )}
      </button>
      <span className={css.itemText}>
        <button
          aria-expanded={detailsOpen}
          className={css.itemNameButton}
          onClick={() => setDetailsOpen((open) => !open)}
          title={detailsOpen ? 'Close the details' : 'Open the details'}
          type="button"
        >
          {item.title}
        </button>
        {clock ? (
          <ItemTimePill
            clock={clock}
            isOpen={timeOpen}
            onToggle={
              onTimeChange ? () => setTimeOpen((open) => !open) : undefined
            }
            title={item.title}
          />
        ) : null}
        <span className={css.itemMeta}>
          {item.origin === 'suggested' ? (
            <span className={css.suggested}>Suggested</span>
          ) : null}
          {meta}
        </span>
      </span>
      <button
        aria-label={`Remove ${item.title}`}
        className={css.remove}
        onClick={onRemove}
        type="button"
      >
        ✕
      </button>
      {timeOpen && clock && onTimeChange ? (
        <ItemTimeEditor
          clock={clock}
          onClose={() => setTimeOpen(false)}
          onSave={(start, end) => onTimeChange(item.id, start, end)}
          title={item.title}
        />
      ) : null}
      {detailsOpen ? (
        <PlannerV3ItemDetails
          item={item}
          leg={leg}
          onCollapse={() => setDetailsOpen(false)}
          onGuestsChange={onGuestsChange}
          party={party}
        />
      ) : null}
      {onCommentChange ? (
        <ItemComment item={item} onChange={onCommentChange} />
      ) : null}
      {services.length > 0 && rates ? (
        <ul aria-label={`Services for ${item.title}`} className={css.services}>
          {services.map((service) => {
            return (
              <li
                className={css.service}
                key={`${service.kind}-${service.unit ?? 0}`}
              >
                <ServiceName
                  group={group}
                  onEditVehicles={
                    service.kind === 'transport' ? onEditVehicles : undefined
                  }
                  fleet={fleet}
                  service={service}
                />
                <span className={css.servicePrice}>
                  <LinePrice
                    amount={
                      plannerServiceRate(service, rates, serviceDay?.(service))
                        .price
                    }
                    category={commissionCategoryOfDayService(service)}
                    currency={serviceCurrency}
                  />
                </span>
                {onRemoveService ? (
                  <button
                    aria-label={`${removeLabel(service)} for ${item.title}`}
                    className={css.remove}
                    onClick={() => onRemoveService(service)}
                    type="button"
                  >
                    ✕
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {vehicleEditor ? (
        <div className={css.vehicleEditor}>{vehicleEditor}</div>
      ) : null}
      {restorable.length > 0 && onRestoreService ? (
        <div className={css.restore}>
          {restorable.map((service) => {
            const name = service.kind === 'guide' ? 'Guide' : 'Transport';
            return (
              <button
                aria-label={`Add ${name} back for ${item.title}`}
                className={css.restoreButton}
                key={service.kind}
                onClick={() => onRestoreService(service)}
                type="button"
              >
                + {name}
              </button>
            );
          })}
        </div>
      ) : null}
    </li>
  );
};
